import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '@/utils/axiosInstance';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, ExternalLink, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import ConfirmDeleteModal from '@/components/ConfirmDeleteModal';

const ResellerRequestsActivity = () => {
  const { t } = useTranslation('admin');
  const { toast } = useToast();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    date_from: '',
    date_to: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 20,
    total_pages: 1
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 300);
    return () => clearTimeout(timer);
  }, [filters, currentPage, itemsPerPage]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      // Add pagination params
      params.append('page', currentPage.toString());
      params.append('page_size', itemsPerPage.toString());

      const response = await axiosInstance.get(`/api/admin/activity/reseller-requests/?${params}`);
      setData(response.data.results || []);
      setPagination({
        count: response.data.count || 0,
        page: response.data.page || 1,
        page_size: response.data.page_size || itemsPerPage,
        total_pages: response.data.total_pages || 1
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500">{t('resellerRequests.approved')}</Badge>;
      case 'rejected':
        return <Badge variant="destructive">{t('resellerRequests.rejected')}</Badge>;
      default:
        return <Badge variant="secondary">{t('resellerRequests.pending')}</Badge>;
    }
  };

  const truncateUrl = (url, maxLength = 60) => {
    if (!url) return '-';
    return url.length > maxLength ? url.substring(0, maxLength) + '...' : url;
  };

  const deleteRequest = async () => {
    const requestId = deleteModal.id;
    if (!requestId) return;
    setUpdatingId(requestId);
    try {
      await axiosInstance.delete(`/api/admin/activity/reseller-requests/${requestId}/delete/`);
      setData(prev => prev.filter(item => item.id !== requestId));
      toast({ description: t('common.deleted') });
      setDeleteModal({ open: false, id: null });
    } catch (error) {
      console.error('Error deleting request:', error);
      toast({ variant: 'destructive', description: t('common.deleteFailed') });
    } finally {
      setUpdatingId(null);
    }
  };

  // Use server-side pagination
  const totalPages = pagination.total_pages;

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('activity.searchPlaceholder')}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="pl-9"
          />
        </div>

        <Select
          value={filters.status || 'all'}
          onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('resellerRequests.filterByStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('resellerRequests.allStatuses')}</SelectItem>
            <SelectItem value="pending">{t('resellerRequests.pending')}</SelectItem>
            <SelectItem value="approved">{t('resellerRequests.approved')}</SelectItem>
            <SelectItem value="rejected">{t('resellerRequests.rejected')}</SelectItem>
          </SelectContent>
        </Select>

        <Input
          type="date"
          value={filters.date_from}
          onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
          className="w-[150px]"
        />

        <Input
          type="date"
          value={filters.date_to}
          onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
          className="w-[150px]"
        />

        <span className="text-sm text-muted-foreground self-center ml-auto text-right">
          {pagination.count} {t('activity.results')}
        </span>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[400px]">{t('resellerRequests.url')}</TableHead>
              <TableHead>{t('resellerRequests.status')}</TableHead>
              <TableHead>{t('activity.createdBy')}</TableHead>
              <TableHead>{t('activity.createdAt')}</TableHead>
              <TableHead className="w-[120px]">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  {t('activity.noData')}
                </TableCell>
              </TableRow>
            ) : (
              data.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline flex items-center gap-1"
                    >
                      {truncateUrl(item.url)}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </TableCell>
                  <TableCell>{getStatusBadge(item.status)}</TableCell>
                  <TableCell>{item.user || '-'}</TableCell>
                  <TableCell>{formatDate(item.created_at)}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteModal({ open: true, id: item.id })}
                      disabled={updatingId === item.id}
                      title={t('common.delete')}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination.count > 0 && (
        <div className="flex items-center justify-between mt-4 px-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">{t('pagination.rowsPerPage')}:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border rounded px-2 py-1 text-sm bg-background"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3"
            >
              {t('pagination.previous')}
            </Button>

            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setCurrentPage(pageNum)}
                  className="w-9 h-9 p-0"
                >
                  {pageNum}
                </Button>
              );
            })}

            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                <span className="px-2 text-muted-foreground">...</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  className="w-9 h-9 p-0"
                >
                  {totalPages}
                </Button>
              </>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-3"
            >
              {t('pagination.next')}
            </Button>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={deleteModal.open}
        onOpenChange={(open) => setDeleteModal({ open, id: open ? deleteModal.id : null })}
        onConfirm={deleteRequest}
        title={t('common.confirmDeleteTitle')}
        description={t('common.confirmDeleteDescription')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        loading={updatingId === deleteModal.id}
      />
    </div>
  );
};

export default ResellerRequestsActivity;
